import React, { useEffect, useContext, useState, useMemo, useCallback } from "react";
import {
  Heading,
  Table,
  TableColumn,
  RoundButton,
  RadioButton,
  Button,
  Tooltip,
  Pill,
  Pager,
  OrderDirection,
  Text,
  TableSort,
  DatePicker,
  CardNotification,
  SkeletonBase,
} from "@foris/avocado-suite";
import LinkRouter from "@common/components/link-router";
import { motion, AnimatePresence } from "motion/react";
import { useHistory } from "react-router-dom";
import { ContextEDH } from "@context/ContextEDH";
import {
  GroupsManagerSearchOrderByFields,
  Label,
  OrderByDirection,
  PageInfo,
} from "@models/ISchema";
import * as mouseflow from "@utils/mouseflow";
import { Context } from "../../context/GroupsManagerContext";
import { TableFiltersReducerType } from "../../context/types";
import { Types as EditionTypes } from "../../context/editions.reducer";
import { Types as NavigationTypes } from "../../context/navigation.reducer";
import { Types as TableFiltersTypes } from "../../context/tableFilters.reducer";
import CapacityInput from "../CapacityInput/CapacityInput";
import Donut from "../Donut/Donut";
import Modal from "@components/modal";
import buildReport from "../../utils/reports";
import savingAllowed from "../../utils/savingAllowed";
import { getReportVariables } from "../../utils/reportFilters";
import { AdaptedGroup, FiltersType } from "../../models";
import { getColumnCell } from "@common/utils/table.util";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import MultiCapacityInput from "../MultiCapacityInput/MultiCapacityInput";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import AutocompleteSelector from "@common/components/autocomplete-selector";
import { useTranslation } from "react-i18next";
import i18n from "@config/i18n";
import css from "./groupsManagerTable.module.scss";
import { getGroupTypeLabel } from "../../utils/groupsUtils";

interface IGroupsManagerTableProps {
  requestGroups: (params: {
    page: number;
    pageSize?: number;
    avoidEditionsCleaning: boolean;
    orderBy: TableFiltersReducerType["orderBy"];
    searchBy: TableFiltersReducerType["searchBy"];
  }) => void;
  pageInfo: PageInfo;
  pageSize?: number;
  activeFilterType: FiltersType;
  isLoading?: boolean;
  lastUpdateDateTime?: string;
  canRefresh?: boolean;
  onRefresh?: () => void;
}

const searchByOptions = () => [
  { label: i18n.t("groups-manager.groups-table.search-by.all"), value: "all" },
  {
    label: i18n.t("groups-manager.groups-table.search-by.group-code"),
    value: GroupsManagerSearchOrderByFields.GroupCode,
  },
  {
    label: i18n.t("groups-manager.groups-table.search-by.group-or-campus"),
    value: GroupsManagerSearchOrderByFields.GroupOrCampus,
  },
  {
    label: i18n.t("groups-manager.groups-table.search-by.course-name"),
    value: GroupsManagerSearchOrderByFields.CourseName,
  },
  {
    label: i18n.t("groups-manager.groups-table.search-by.main-instructor"),
    value: GroupsManagerSearchOrderByFields.MainInstructor,
  },
  {
    label: i18n.t("groups-manager.groups-table.search-by.group-type"),
    value: GroupsManagerSearchOrderByFields.GroupType,
  },
];

const getSearchByPayload = (query: string, field: string) => {
  if (!query || !field) {
    return {
      fields: [],
      text: "",
    };
  }

  if (field === "all") {
    const fields =
      Object.values(searchByOptions())
        ?.map(option => option?.value)
        ?.filter(option => option !== "all") ?? [];

    return {
      fields,
      text: query,
    };
  }

  return {
    fields: [field],
    text: query,
  };
};

enum DownloadMode {
  All = "all",
  ByDate = "by_date",
}

let filterType: FiltersType = null;

const isGroupAllowedToEdit = (group: AdaptedGroup) => {
  const isParentType = !!group?.referentType;

  if (isParentType && filterType !== "PACKAGE") {
    return false;
  }

  const isValidGroupType = isParentType ? group?.referentType !== "CAMPUS" : true;
  const isEditionAllowed = isValidGroupType && group?.isEditable?.allowed && group?.isActive;

  return isEditionAllowed;
};

const GroupsManagerTable = ({
  requestGroups,
  pageInfo,
  pageSize,
  isLoading,
  activeFilterType,
  lastUpdateDateTime,
  canRefresh,
  onRefresh,
}: IGroupsManagerTableProps) => {
  const history = useHistory();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { state: outerState } = useContext(ContextEDH);
  const { state, dispatch } = useContext(Context);
  const [displayModal, setDisplayModal] = useState(false);
  const [displayReportModal, setDisplayReportModal] = useState(false);
  const [startDate, setStartDate] = useState<Date>(null);
  const [endDate, setEndDate] = useState<Date>(null);
  const [downloadMode, setDownloadMode] = useState<DownloadMode>(DownloadMode.All);
  const [onModalClick, setOnModalClick] = useState({
    primary: () => {
      /* pass */
    },
    secondary: () => {
      /* pass */
    },
  });
  const [orderByReport, setOrderByReport] = useState<TableFiltersReducerType["orderBy"]>(null);
  const [searchByReport, setSearchByReport] = useState<TableFiltersReducerType["searchBy"]>(null);
  const [currentSearchBy, setCurrentSearchBy] = useState<{ query: string; option: any } | null>(
    null,
  );
  const stringifiedFilters = JSON.stringify(
    getReportVariables(state?.filters, orderByReport, searchByReport),
    null,
  );

  const canEdit = outerState?.base?.base?.user?.abilities?.can_edit_groups_in_group_manager;
  const isDefaultBehavior = outerState?.base?.groupsManagerType === "SHARED";

  const showPackageCalendarLink =
    state?.filters?.selectedPackage != null &&
    state?.filtersSelectors?.visibleFilter === FiltersType.PACKAGE;

  const { origin, scenario, contextUrl } = useContextUrl();
  const { editorUrl } = useNavigationUrl();

  const selectedTableRowsCount = useMemo(() => {
    return Object.keys(state?.editions?.groupsToEditById ?? {})?.length ?? 0;
  }, [state?.editions?.groupsToEditById]);

  const groupIsVisible = (group: AdaptedGroup): boolean => {
    return state?.editions?.byGroupId[group?.id]?.visibleForEnrollment != undefined
      ? state?.editions?.byGroupId[group?.id]?.visibleForEnrollment
      : group?.visibleForEnrollment;
  };

  const pagination = useMemo(() => {
    return {
      onChange: (page: number) =>
        requestGroups?.({
          page,
          pageSize: pageSize ?? pageInfo?.size ?? undefined,
          avoidEditionsCleaning: false,
          orderBy: state?.tableFilters?.orderBy,
          searchBy: state?.tableFilters?.searchBy,
        }),
      showRowsPerPage: pageSize !== undefined,
      pageSizeOptions: [5, 10, 20],
      onRowsPerPageChange: option =>
        requestGroups?.({
          page: 1,
          pageSize: option?.value,
          avoidEditionsCleaning: false,
          orderBy: state?.tableFilters?.orderBy,
          searchBy: state?.tableFilters?.searchBy,
        }),
      total: pageInfo?.total ?? 0,
      page: pageInfo?.page ?? 0,
      size: pageSize ?? pageInfo?.size,
    };
  }, [pageInfo, state?.tableFilters?.orderBy, state?.tableFilters?.searchBy]);

  useEffect(() => {
    filterType = activeFilterType;
  }, [activeFilterType]);

  const handleSortChange = useCallback(
    (newSort: TableSort) => {
      const newOrderBy = {
        header: "" as any,
        field: newSort?.column as GroupsManagerSearchOrderByFields,
        direction: newSort?.direction?.toUpperCase() as OrderByDirection,
      };

      setOrderByReport(newOrderBy);
      setSearchByReport(state?.tableFilters?.searchBy);

      dispatch({
        type: TableFiltersTypes.SetOrderBy,
        payload: newOrderBy,
      });

      requestGroups?.({
        page: pageInfo?.page,
        avoidEditionsCleaning: false,
        orderBy: newOrderBy,
        searchBy: state?.tableFilters?.searchBy,
      });
    },
    [pageInfo?.page, state?.tableFilters?.searchBy],
  );

  /**
   * Set the `groupIdsToExludeFromCapacityEditions` after groups are requested.
   * Groups are exluded if they're "NAL" or are not editable.
   */
  const groupIdsToExludeFromCapacityEditions = useMemo(() => {
    const groupIdsToExclude = (state?.data?.groups ?? []).reduce((acc, group) => {
      if (group?.campus?.code === "NAL" || !group?.isEditable?.allowed) {
        acc.add(group?.id);
      }
      return acc;
    }, new Set<string>());

    return groupIdsToExclude;
  }, [state?.data?.groups]);

  /**
   * Given a callback, display the Modal component if any edition has been
   * made and the editions *can be saved*, asking for confirmation.
   *
   * If the user confirms, the given callback it's
   * executed, if the user "cancels" the callback isn't executed and the modal
   * disappears.
   */

  const onValidateChangesToSave = useCallback(
    (callback: () => void) => {
      const hasChangesToSave =
        Object.keys(state?.editions?.byGroupId || {}).length > 0 &&
        savingAllowed(state?.editions?.errorsByGroupId || {});

      if (hasChangesToSave) {
        setDisplayModal(true);
        setOnModalClick({
          primary: () => {
            setDisplayModal(false);
          },
          secondary: () => {
            setDisplayModal(false);
            dispatch({ type: EditionTypes.Clean });
            callback?.();
          },
        });
        return;
      }

      callback?.();
    },
    [state?.editions?.byGroupId, state?.editions?.errorsByGroupId],
  );

  const handleSeeDetails = (group: AdaptedGroup) => {
    const campus = group?.campus?.code;
    const isParentType = group?.isReferent;
    const subgroupsPathname = `/editor/groups-manager/subgroups/${contextUrl}/${group?.id}`;
    const pathname = `/editor/groups-manager/${contextUrl}/${group?.id}`;

    onValidateChangesToSave(() => {
      dispatch({
        type: NavigationTypes.AddToLocationStack,
        payload: {
          view: `/editor/groups-manager/${contextUrl}`,
          page: state?.data?.groupsPageInfo?.page ?? 1,
        },
      });

      if (campus === "NAL" || isParentType) {
        mouseflow.actionTag(
          "action_groups_manager_subgroups",
          outerState?.base?.isMouseflowEnabled,
        );
        history.push(subgroupsPathname);
      } else {
        mouseflow.actionTag(
          "action_groups_manager_enrollments",
          outerState?.base?.isMouseflowEnabled,
        );
        history.push(pathname);
      }
    });
  };

  const columnsSuite = useMemo(() => {
    const columns = [
      {
        header: "",
        renderCell: (group: AdaptedGroup) => (
          <Tooltip label={t("groups-manager.groups-table.table.headers.see-details")}>
            <RoundButton
              size="sm"
              variant="ghost"
              icon="external-link"
              onClick={() => handleSeeDetails(group)}
            />
          </Tooltip>
        ),
      },
      {
        key: "groupCode",
        isSortable: true,
        header: t("groups-manager.groups-table.table.headers.group-code"),
        renderCell: (group: AdaptedGroup) => getColumnCell(group?.clientCode || group?.code || "-"),
        desktopOptions: {
          headCellStyles: {
            minWidth: "100px",
          },
        },
      },
      {
        key: "groupOrCampus",
        isSortable: true,
        header: t("groups-manager.groups-table.table.headers.group-or-campus"),
        renderCell: (group: AdaptedGroup) => {
          const campus = group?.campus?.code;
          const groupLabel = group?.label;

          return getColumnCell(`${groupLabel} | ${campus}`);
        },
        desktopOptions: {
          headCellStyles: {
            minWidth: "160px",
          },
        },
      },
      {
        key: "courseName",
        isSortable: true,
        header: t("groups-manager.groups-table.table.headers.course-name"),
        renderCell: (group: AdaptedGroup) => {
          const code = group?.course?.code;
          const name = group?.course?.name;

          return getColumnCell(!!code || !!name ? [code, name].join(" | ") : "-");
        },
        desktopOptions: {
          headCellStyles: {
            minWidth: "220px",
          },
        },
      },
      {
        key: "mainInstructor",
        isSortable: true,
        header: t("groups-manager.groups-table.table.headers.main-instructor"),
        renderCell: (group: AdaptedGroup) => {
          const code = group?.primaryInstructor?.code;
          const name = group?.primaryInstructor?.name;

          return getColumnCell(
            !!code || !!name
              ? [code, name].join(" | ")
              : t("groups-manager.groups-table.table.rows.not-assigned"),
          );
        },
        desktopOptions: {
          headCellStyles: {
            minWidth: "170px",
          },
        },
      },
      {
        key: "state",
        isSortable: true,
        header: t("groups-manager.groups-table.table.headers.state"),
        renderCell: (item: AdaptedGroup) => (
          <Pill className={css.columnPill}>
            {item?.isActive
              ? t("groups-manager.groups-table.table.rows.status.active")
              : t("groups-manager.groups-table.table.rows.status.inactive")}
          </Pill>
        ),
        desktopOptions: {
          headCellStyles: {
            minWidth: "100px",
          },
        },
      },
      {
        key: "capacity",
        isSortable: true,
        header: t("groups-manager.groups-table.table.headers.capacity"),
        renderCell: (group: AdaptedGroup) => {
          return (
            <CapacityInput
              group={group}
              disabled={!isGroupAllowedToEdit(group) || !canEdit}
              isSubgroup={false}
            />
          );
        },
        desktopOptions: {
          headCellStyles: {
            minWidth: "100px",
          },
        },
      },
      {
        header: t("groups-manager.groups-table.table.headers.utilization"),
        renderCell: (group: AdaptedGroup) => <Donut group={group} padding={"0.75rem 0"} />,
        desktopOptions: {
          headCellStyles: {
            minWidth: "130px",
          },
        },
      },
      {
        header: t("groups-manager.groups-table.table.headers.calculated-capacity"),
        renderCell: (group: AdaptedGroup) =>
          getColumnCell(group?.groupCapacity?.calculatedCapacity ?? "-"),
        desktopOptions: {
          headCellClassName: css.rightColumn,
          bodyCellClassName: css.rightColumn,
          headCellStyles: {
            minWidth: "110px",
          },
        },
      },
      {
        key: "groupType",
        isSortable: true,
        header: t("groups-manager.groups-table.table.headers.group-type"),
        renderCell: (group: AdaptedGroup) => {
          const isNAL = group?.campus?.code === "NAL";
          const isParentGroup = group?.isReferent;
          const groupTypeLabel = getGroupTypeLabel(isNAL ? "NATIONAL" : group?.referentType);

          if (groupTypeLabel) {
            return (
              <Pill className={css.groupTypePill} icon={isParentGroup ? "branch" : undefined}>
                {groupTypeLabel}
              </Pill>
            );
          }

          return "";
        },
      },
    ];

    if (!isDefaultBehavior) {
      columns.splice(4, 0, {
        header: t("groups-manager.groups-table.table.headers.group-subterm"),
        renderCell: (group: AdaptedGroup) => {
          const isPmtLabel = (label: Label) => (label?.code ?? "")?.toLowerCase()?.includes("pmt");
          const groupSubterms =
            (group?.labels ?? [])
              .filter(isPmtLabel)
              .map(label => label.code)
              .join(",") || "-";

          return getColumnCell(groupSubterms);
        },
        desktopOptions: {
          headCellStyles: {
            minWidth: "100px",
          },
        },
      });

      columns.splice(10, 0, {
        header: t("groups-manager.groups-table.table.headers.min-max"),
        renderCell: (group: AdaptedGroup) => {
          const min = group?.groupCapacity?.configMinCapacity ?? 0;
          const max = group?.groupCapacity?.configMaxCapacity ?? 0;

          return getColumnCell(!min || !max ? "N/A" : `${min} - ${max}`);
        },
        desktopOptions: {
          headCellClassName: css.rightColumn,
          bodyCellClassName: css.rightColumn,
          headCellStyles: {
            minWidth: "130px",
          },
        },
      });
    }

    return columns;
  }, [language, canEdit]);

  /**
   * Clean context's groupsToEdit when the table is initialized
   */
  useEffect(() => {
    dispatch({ type: EditionTypes.SetGroupsToEdit, payload: {} });
    dispatch({
      type: TableFiltersTypes.SetSearchByFields,
      payload: [],
    });
    dispatch({ type: TableFiltersTypes.SetSearchByText, payload: "" });
  }, []);

  useEffect(() => {
    if (state?.filtersSelectors?.usedFilterToSearch !== "KEY") {
      setSearchByReport({ fields: [], text: "" });
      setCurrentSearchBy(null);
    }
  }, [state?.data?.groups]);

  const groupReportUrl = useMemo(() => {
    return `${buildReport("groups", {
      context: { origin, scenario },
    })}${stringifiedFilters ? `&filter='${stringifiedFilters}'` : ""}`;
  }, [state?.filters, stringifiedFilters, origin, scenario]);

  const groupsByIdAllowedToEdit = useMemo(() => {
    return (state?.data?.groups ?? []).filter(isGroupAllowedToEdit).reduce((acc, group) => {
      acc[group?.id] = group;
      return acc;
    }, {});
  }, [state?.data?.groups]);

  const hasAllVisibleGroupsSelected = useMemo(() => {
    const groups = Object.keys(groupsByIdAllowedToEdit ?? {}) ?? [];
    const selectedGroups = Object.keys(state?.editions?.groupsToEditById ?? {}) ?? [];
    const visibleGroups = [...groups].every(group => selectedGroups.includes(group));

    return !!selectedGroups?.length && visibleGroups;
  }, [state?.editions?.groupsToEditById, state?.data?.groups, groupsByIdAllowedToEdit]);

  return (
    <section className={css.groupsManagerTable}>
      {!!pageInfo && (pageInfo?.total > 0 || !!currentSearchBy) && (
        <>
          <div className={css.groupsManagerTable_header}>
            <Heading type="h2" className={css.headerTitle}>
              {isLoading ? (
                <SkeletonBase height="24px" width="90px" />
              ) : state?.data?.groupsPageInfo?.total === 1 ? (
                t("groups-manager.groups-table.count-title-single")
              ) : (
                t("groups-manager.groups-table.count-title", {
                  count: state?.data?.groupsPageInfo?.total ?? 0,
                })
              )}
            </Heading>

            <div className={css.headerRight}>
              {!!lastUpdateDateTime && (
                <Text className={css.headerRight_lastUpdate}>
                  <b>{t("groups-manager.groups-table.last-update")}:</b> {lastUpdateDateTime}
                </Text>
              )}

              {showPackageCalendarLink && (
                <LinkRouter
                  className={css.actionLink}
                  iconLeft="external-link"
                  to={editorUrl("package", state?.filters?.selectedPackage)}
                  size="md"
                  colored
                >
                  {t("groups-manager.groups-table.package-link")}
                </LinkRouter>
              )}

              <Button
                className={css.headerRight_refresh}
                size="sm"
                variant="ghost"
                leftIcon="refresh"
                disabled={isLoading || !canRefresh}
                onClick={onRefresh}
              >
                {t("groups-manager.groups-table.update-button")}
              </Button>

              <Button
                size="sm"
                variant="ghost"
                leftIcon="download"
                disabled={isLoading}
                onClick={() => {
                  mouseflow.actionTag(
                    "action_groups_manager_groups_report",
                    outerState?.base?.isMouseflowEnabled,
                  );
                  window.open(groupReportUrl, "_blank");
                }}
              >
                {t("groups-manager.groups-table.download-report")}
              </Button>

              <Button
                size="sm"
                variant="ghost"
                leftIcon="download"
                disabled={isLoading}
                onClick={() => setDisplayReportModal(true)}
              >
                {t("groups-manager.groups-table.download-history")}
              </Button>
            </div>
          </div>
        </>
      )}

      <div className={css.groupsManagerTable_table}>
        <AnimatePresence>
          {selectedTableRowsCount > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className={css.tableMultipleEdit}
            >
              <Heading type="h4" className={css.tableMultipleEdit_title}>
                {t("groups-manager.groups-table.actions.selected-rows", {
                  count: selectedTableRowsCount ?? 0,
                })}
              </Heading>

              <div className={css.tableMultipleEdit_actions}>
                <Button
                  size="sm"
                  leftIcon="open-eye"
                  variant="ghost"
                  disabled={selectedTableRowsCount === 0 || !canEdit}
                  onClick={() => {
                    dispatch({
                      type: EditionTypes.EditGroupsToEditVisibilities,
                      payload: state?.data?.groupsById,
                    });
                  }}
                >
                  {t("groups-manager.groups-table.actions.change-visibility")}
                </Button>

                <MultiCapacityInput
                  disabled={selectedTableRowsCount === 0 || !canEdit}
                  getGroupIdsToExclude={groupIdsToExludeFromCapacityEditions}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {isLoading && (
          <DimensionsSearchSkeleton
            showHeader={!pageInfo || pageInfo?.total === 0}
            showPagination={pageInfo?.hasNextPage || pageInfo?.hasPreviousPage}
            rows={pageSize ?? pageInfo?.size ?? 5}
            columns={columnsSuite?.length}
          />
        )}

        {!isLoading &&
          !!pageInfo &&
          state?.filtersSelectors?.usedFilterToSearch === "KEY" &&
          state?.filtersSelectors?.visibleFilter === "KEY" && (
            <AutocompleteSelector
              className={css.tableSearch}
              initialOption={currentSearchBy?.option ?? undefined}
              initialInputValue={currentSearchBy?.query ?? undefined}
              isClearable={!!currentSearchBy?.query}
              options={searchByOptions()}
              onSearch={payload => {
                const newSearchBy = getSearchByPayload(payload?.query, payload?.option?.value);
                setSearchByReport(newSearchBy as any);
                setCurrentSearchBy(payload);

                dispatch({
                  type: TableFiltersTypes.SetSearchByFields,
                  payload: newSearchBy?.fields ?? ([] as any),
                });
                dispatch({
                  type: TableFiltersTypes.SetSearchByText,
                  payload: newSearchBy?.text ?? "",
                });

                requestGroups?.({
                  page: 1,
                  avoidEditionsCleaning: false,
                  orderBy: state?.tableFilters?.orderBy,
                  searchBy: newSearchBy as any,
                });
              }}
              onClearSearch={() => {
                setSearchByReport({ fields: [], text: "" });
                setCurrentSearchBy(null);

                dispatch({
                  type: TableFiltersTypes.SetSearchByFields,
                  payload: [],
                });
                dispatch({ type: TableFiltersTypes.SetSearchByText, payload: "" });

                requestGroups?.({
                  page: 1,
                  avoidEditionsCleaning: false,
                  orderBy: state?.tableFilters?.orderBy,
                  searchBy: { fields: [], text: "" },
                });
              }}
            />
          )}

        {!!pageInfo && !!pageInfo?.total && !isLoading && (
          <>
            <Table
              data={state?.data?.groups ?? []}
              columns={columnsSuite as TableColumn[]}
              initialSort={{
                direction: (state?.tableFilters?.orderBy?.direction?.toLowerCase() ??
                  "desc") as OrderDirection,
                column: state?.tableFilters?.orderBy?.field ?? "groupCode",
              }}
              onSortChange={handleSortChange}
              rowOptions={{
                selectorType: "checkbox",
                isAllSelected: hasAllVisibleGroupsSelected,
                isHeadSelectorDisabled:
                  Object.keys(groupsByIdAllowedToEdit ?? {}).length === 0 || !canEdit,
                isSelectorDisabled: (row: unknown) =>
                  !isGroupAllowedToEdit(row as AdaptedGroup) || !canEdit,
                isSelected: (row: unknown) =>
                  (row as AdaptedGroup)?.id in (state?.editions?.groupsToEditById ?? {}),
                onRowSelect(isChecked, data) {
                  const group = data as AdaptedGroup;
                  const type = isChecked
                    ? EditionTypes.AddGroupToEdit
                    : EditionTypes.RemoveGroupToEdit;

                  dispatch({ type, payload: group });
                },
                onSelectAll(isChecked) {
                  if (!isChecked) {
                    dispatch({ type: EditionTypes.SetGroupsToEdit, payload: {} });
                  } else {
                    dispatch({
                      type: EditionTypes.SetGroupsToEdit,
                      payload: groupsByIdAllowedToEdit,
                    });
                  }
                },
              }}
              rightActions={[
                {
                  icon: row => (groupIsVisible(row as AdaptedGroup) ? "open-eye" : "closed-eye"),
                  isDisabled: row => !isGroupAllowedToEdit(row as AdaptedGroup) || !canEdit,
                  onClick: row => {
                    const group = row as AdaptedGroup;

                    dispatch({
                      type: EditionTypes.UpdateGroupVisibility,
                      payload: {
                        groupId: group?.id,
                        value: !groupIsVisible(group),
                        originalValue: group?.visibleForEnrollment,
                      },
                    });
                  },
                },
              ]}
            />

            <Pager
              classname={{
                global: css.tablePager,
              }}
              {...pagination}
            />
          </>
        )}

        {!!pageInfo && !pageInfo?.total && !isLoading && (
          <CardNotification
            className={css.groupsManagerApp_noResults}
            state="info"
            title={t("groups-manager.groups-table.empty-results.title")}
            outlined
          >
            {t("groups-manager.groups-table.empty-results.description-search")}
          </CardNotification>
        )}
      </div>

      <Modal
        typeState="confirm"
        title={t("groups-manager.not-saved-changes-modal.title")}
        show={displayModal}
        textButtonPrincipal={t("groups-manager.not-saved-changes-modal.principal-button")}
        textButtonSecondary={t("groups-manager.not-saved-changes-modal.secondary-button")}
        onClickPrincipal={onModalClick?.primary}
        onClickSecondary={onModalClick?.secondary}
        onClose={onModalClick?.primary}
      >
        <Text>{t("groups-manager.not-saved-changes-modal.description")}</Text>
      </Modal>

      <Modal
        typeState="confirm"
        title={t("groups-manager.generate-history-modal.title")}
        show={displayReportModal}
        textButtonPrincipal={t("groups-manager.generate-history-modal.principal-button")}
        textButtonSecondary={t("groups-manager.generate-history-modal.secondary-button")}
        className={css.reportModal}
        onClickPrincipal={() => {
          const reportLink = buildReport("changeHistory", {
            filters: state?.filters,
            dates: { startDate, endDate },
            context: { origin, scenario },
          });
          setDisplayReportModal(false);
          mouseflow.actionTag(
            "action_groups_manager_change_history_report",
            outerState?.base?.isMouseflowEnabled,
          );
          window.open(reportLink, "_blank");
          setDownloadMode(DownloadMode.All);
        }}
        onClickSecondary={() => {
          setDownloadMode(DownloadMode.All);
          setDisplayReportModal(false);
        }}
        onClose={() => {
          setDownloadMode(DownloadMode.All);
          setDisplayReportModal(false);
        }}
      >
        <div className={css.reportModal_content}>
          <RadioButton
            labelRight={t("groups-manager.generate-history-modal.download-all")}
            onChange={() => setDownloadMode(DownloadMode.All)}
            checked={downloadMode === DownloadMode.All}
          />
          <RadioButton
            labelRight={t("groups-manager.generate-history-modal.download-by-date")}
            onChange={() => setDownloadMode(DownloadMode.ByDate)}
            checked={downloadMode === DownloadMode.ByDate}
          />

          <div className={css.dateSelectors}>
            <DatePicker
              placeholder="yyyy-mm-dd"
              label={t("groups-manager.generate-history-modal.start-date")}
              disabled={downloadMode == DownloadMode.All}
              selectedDate={startDate ? startDate : null}
              onChange={setStartDate}
            />

            <DatePicker
              placeholder="yyyy-mm-dd"
              label={t("groups-manager.generate-history-modal.end-date")}
              disabled={downloadMode == DownloadMode.All}
              selectedDate={endDate ? endDate : null}
              minDate={startDate ? startDate : undefined}
              onChange={setEndDate}
            />
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default GroupsManagerTable;
